import { Workflow } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, useLocation } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { PipelineView } from "./pipeline/PipelineView";
import { TasksView } from "./tasks/TasksView";

const TASKS_PATH = "/operations/tasks";

/** CRM | TASKS switch, like the Jivo top-left selector. */
const OperationsTabs = ({ isTasks }: { isTasks: boolean }) => {
  const translate = useTranslate();
  const tabClassName = (isActive: boolean) =>
    cn(
      "rounded-md px-3 py-1 text-sm font-semibold uppercase tracking-wide transition-colors",
      isActive
        ? "bg-background text-foreground shadow-xs"
        : "text-muted-foreground hover:text-foreground",
    );
  return (
    <nav
      className="inline-flex rounded-lg bg-muted p-1"
      aria-label={translate("crm.operations.title")}
    >
      <Link
        to={OperationsPage.path}
        className={tabClassName(!isTasks)}
        aria-current={!isTasks ? "page" : undefined}
      >
        {translate("crm.operations.tabs.crm")}
      </Link>
      <Link
        to={TASKS_PATH}
        className={tabClassName(isTasks)}
        aria-current={isTasks ? "page" : undefined}
      >
        {translate("crm.operations.tabs.tasks")}
      </Link>
    </nav>
  );
};

export const OperationsPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isTasks = location.pathname.startsWith(TASKS_PATH);
  const content = isTasks ? <TasksView /> : <PipelineView />;

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex min-w-0 flex-1">
            <h1 className="text-xl font-semibold">
              {translate("crm.operations.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="flex flex-col gap-3 pb-6">
            <OperationsTabs isTasks={isTasks} />
            {content}
          </div>
        </MobileContent>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex items-center gap-4">
        <OperationsTabs isTasks={isTasks} />
        <h1 className="flex items-center gap-2 text-lg font-semibold text-muted-foreground">
          <Workflow className="size-5" />
          {translate("crm.operations.title")}
        </h1>
      </div>
      {content}
    </div>
  );
};

OperationsPage.path = "/operations";
